import { useCallback, useEffect, useMemo } from "react"
import { useField, useFormikContext } from "formik"
import { ConfigQuery, fetchSatelliteCanteens, SatelliteCanteensResponse } from "../../../createOrderApi"
import { ApiQueryAutocomplete } from "../../../../../../core/api/components/ApiQueryAutocomplete"
import { SatelliteDetails, SatelliteType } from "../../../createOrderTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ExtendedDynamicConfigKey } from "../../form/formTypes"
import { getFormikFieldError } from "../../form/helpers"

function transformData(data: SatelliteCanteensResponse) {
    return data?.satellites ?? []
}

type SatelliteCanteensProps = Readonly<{
    name: ExtendedDynamicConfigKey
    label: string
    userId: number
    merchantId: number
    satelliteType: SatelliteType
}>

export function SatelliteCanteens({ name, label, userId, merchantId, satelliteType }: SatelliteCanteensProps) {
    const queryDeps = useMemo(
        () => ({
            userId,
            merchantId,
            type: satelliteType,
        }),
        [userId, merchantId, satelliteType],
    )

    const {
        data: options = [],
        isError,
        isLoading,
    } = useApiQuery(fetchSatelliteCanteens, {
        queryName: ConfigQuery.MEETING_SERVICE_SATELLITE_CANTEENS,
        dependencies: queryDeps,
        select: transformData,
    })

    const validateSatellite = useCallback(
        (value: SatelliteDetails | null) => {
            if (options.length > 0 && !value) return `${label} is required`
            return undefined
        },
        [options.length],
    )

    const [field, meta, helpers] = useField<SatelliteDetails | null>({ name, validate: validateSatellite })
    const { submitCount } = useFormikContext<any>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)

    const optionsLength = options.length
    const currentId = field.value?.id ?? null
    const hasCurrent = currentId == null ? false : options.some((o) => o.id === currentId)
    const singleOption = optionsLength === 1 ? options[0] : null

    useEffect(() => {
        if (isLoading) return

        if (optionsLength === 0) {
            if (field.value !== null) helpers.setValue(null)
            if (meta.error) helpers.setError(undefined)
            return
        }

        if (field.value && !hasCurrent) {
            helpers.setValue(null)
            if (meta.error) helpers.setError(undefined)
            return
        }

        if (!field.value && singleOption) {
            helpers.setValue(singleOption)
        }
    }, [isLoading, optionsLength, hasCurrent, singleOption?.id, currentId, helpers, meta.error, field.value])

    const handleChange = (_e: React.SyntheticEvent, value: SatelliteDetails | null) => {
        helpers.setValue(value ?? null)
    }

    if (!options.length) return null

    return (
        <ApiQueryAutocomplete
            isLoading={isLoading}
            isError={isError}
            options={options}
            value={field.value || null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            onChange={handleChange}
            label={label}
            size="small"
            slotProps={{
                textField: formikErrorProps,
            }}
            filterOptions={(x) => x}
        />
    )
}
